'use client';

import { c as _c } from "react/compiler-runtime";
import { useMutation, type UseMutationResult, useQuery, type UseQueryResult, useQueryClient } from '@tanstack/react-query';
import * as clients from 'shared/api/clients';
import type * as fetch from 'shared/api/fetch';
import * as messages from 'shared/api/messages';
import * as notifications from 'shared/api/notifications';
import * as users from 'shared/api/users';
import { type PatchClientReq, type PatchUpdateMemberRoleReq, type MemberResp } from 'shared/api/clients';
import { type UserNotificationResp, type NotificationCountResp } from 'shared/api/notifications';
export const queryKeys = {
  allMembers: 'allMembers',
  verifyToken: 'verifyToken',
  member: 'member',
  allMessages: 'allMessages',
  allClients: 'allClients',
  allNotifications: 'allNotifications',
  unreadNotificationsCount: 'unreadNotificationsCount',
  client: 'client',
  markNotificationAsRead: 'markNotificationAsRead'
};
export const useCreateNewClient = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: clients.createClient,
      onMutate: newClient => {
        const prevClientsList = queryClient.getQueryData([queryKeys.allClients]);
        if (prevClientsList !== undefined) {
          queryClient.setQueryData([queryKeys.allClients], [...prevClientsList, newClient]);
        }
        return {
          prevClientsList
        };
      },
      onError: async (_, __, context) => {
        if (context?.prevClientsList != null) {
          queryClient.setQueryData([queryKeys.allClients], [...context.prevClientsList]);
        }
      },
      onSettled: async () => {
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.allMembers]
        });
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.allClients]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export interface Member {
  name: string;
  email: string;
  id: number;
  role: 'viewer' | 'editor' | 'admin';
  avatarUrl?: string;
  createdAt: string;
  updatedAt: string;
}
export interface MembersResp extends Array<Member> {}
export const useQueryMembers = clientId => {
  const $ = _c(7);
  let t0;
  let t1;
  if ($[0] !== clientId) {
    t0 = [queryKeys.allMembers, clientId];
    t1 = async () => await clients.getMembers(clientId);
    $[0] = clientId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  const t2 = Boolean(clientId);
  let t3;
  if ($[3] !== t0 || $[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return useQuery(t3) as UseQueryResult<MembersResp, fetch.ClientError>;
};
export const useQueryMessages = clientId => {
  const $ = _c(7);
  let t0;
  let t1;
  if ($[0] !== clientId) {
    t0 = [queryKeys.allMessages, clientId];
    t1 = async () => await messages.getMessages(clientId);
    $[0] = clientId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  const t2 = Boolean(clientId);
  let t3;
  if ($[3] !== t0 || $[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return useQuery(t3) as unknown as UseQueryResult<messages.MessagesResp, fetch.ClientError>;
};
export const useQueryMember = (clientId, userId, t0) => {
  const $ = _c(8);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  let t2;
  if ($[0] !== clientId || $[1] !== userId) {
    t1 = [queryKeys.member, clientId, userId];
    t2 = async () => await clients.getMember(clientId, userId as number);
    $[0] = clientId;
    $[1] = userId;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  const t3 = Boolean(clientId) && userId != null && enabled;
  let t4;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      throwOnError: false
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return useQuery(t4);
};
export const useCreateMessage = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: messages.createMessage,
      onError: async () => {
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.allMessages]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useResendMessage = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: messages.resendMessage
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useQueryClients = t0 => {
  const $ = _c(3);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [queryKeys.allClients];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== enabled) {
    t2 = {
      queryKey: t1,
      queryFn: _temp,
      enabled
    };
    $[1] = enabled;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return useQuery(t2);
};
const getFullKey = (key: string, clientId: number): [string, number] => {
  return [key, clientId];
};
export const useMarkNotificationAsRead = () => {
  const $ = _c(3);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [queryKeys.markNotificationAsRead];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== queryClient) {
    t1 = {
      mutationKey: t0,
      mutationFn: _temp2,
      onMutate: async t2 => {
        const {
          userNotificationIds: userNotificationIds_0,
          clientId: clientId_0
        } = t2;
        const allNotificationsFullKey = getFullKey(queryKeys.allNotifications, clientId_0);
        await queryClient.cancelQueries({
          queryKey: allNotificationsFullKey
        });
        const previousNotifications = queryClient.getQueryData(allNotificationsFullKey);
        if (previousNotifications != null) {
          const updated = previousNotifications.map(n => userNotificationIds_0.includes(n.id) ? {
            ...n,
            isRead: true,
            updatedAt: new Date().toISOString()
          } : n);
          queryClient.setQueryData(allNotificationsFullKey, updated);
        }
        const previousCount = queryClient.getQueryData([queryKeys.unreadNotificationsCount, clientId_0]);
        if (previousCount != null) {
          const newCount = Math.max(0, previousCount.count - userNotificationIds_0.length);
          queryClient.setQueryData([queryKeys.unreadNotificationsCount, clientId_0], {
            ...previousCount,
            count: newCount
          });
        }
        return {
          previousNotifications,
          previousCount,
          clientId: clientId_0
        };
      },
      onError: (_err, _vars, context) => {
        if (context == null) {
          return;
        }
        const allNotificationsFullKey_0 = getFullKey(queryKeys.allNotifications, context.clientId);
        const unreadNotificationsCountFullKey = getFullKey(queryKeys.unreadNotificationsCount, context.clientId);
        if (context.previousNotifications != null) {
          queryClient.setQueryData(allNotificationsFullKey_0, context.previousNotifications);
        }
        if (context.previousCount != null) {
          queryClient.setQueryData(unreadNotificationsCountFullKey, context.previousCount);
        }
      },
      onSettled: async (_data, _error, _variables, context_0) => {
        if (context_0 == null) {
          return;
        }
        const allNotificationsFullKey_1 = getFullKey(queryKeys.allNotifications, context_0.clientId);
        const unreadNotificationsCountFullKey_0 = getFullKey(queryKeys.unreadNotificationsCount, context_0.clientId);
        await queryClient.invalidateQueries({
          queryKey: allNotificationsFullKey_1
        });
        await queryClient.invalidateQueries({
          queryKey: unreadNotificationsCountFullKey_0
        });
      }
    };
    $[1] = queryClient;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useMutation(t1);
};
export const useQueryNotifications = (clientId, t0, t1) => {
  const $ = _c(8);
  const unreadOnly = t0 === undefined ? true : t0;
  const enabled = t1 === undefined ? true : t1;
  let t2;
  let t3;
  if ($[0] !== clientId || $[1] !== unreadOnly) {
    t2 = [...getFullKey(queryKeys.allNotifications, clientId), unreadOnly];
    t3 = async () => await notifications.getNotifications(clientId, unreadOnly);
    $[0] = clientId;
    $[1] = unreadOnly;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] !== enabled || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      enabled
    };
    $[4] = enabled;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return useQuery(t4);
};
export const useQueryUnreadNotificationsCount = (clientId, t0) => {
  const $ = _c(7);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  let t2;
  if ($[0] !== clientId) {
    t1 = [queryKeys.unreadNotificationsCount, clientId];
    t2 = async () => await notifications.getUnreadNotificationsCount(clientId);
    $[0] = clientId;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] !== enabled || $[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2,
      enabled,
      throwOnError: false
    };
    $[3] = enabled;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return useQuery(t3);
};
export const useQueryClientsWithField = (t0, field) => {
  const $ = _c(7);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  let t2;
  if ($[0] !== field) {
    t1 = [queryKeys.allClients, field];
    t2 = async () => await clients.getClientsWithField(field);
    $[0] = field;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] !== enabled || $[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2,
      enabled,
      staleTime: 0
    };
    $[3] = enabled;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return useQuery(t3);
};
export const useUpdateClient = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: clients.updateClient,
      onSuccess: async data => {
        queryClient.setQueryData([queryKeys.allClients], prevClientsList => {
          if (prevClientsList !== undefined) {
            return prevClientsList.map(client => {
              if (client.id === data.id) {
                return data;
              }
              return client;
            });
          }
          return [];
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useUpdateMemberRole = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: clients.updateMemberRole,
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.allMembers]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useUpdateMessage = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: messages.updateMessage,
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.allMessages]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useUpdateMemberNotificationSettings = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: clients.updateMemberNotificationSettings
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useUpdateMember = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: clients.updateMember
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useDeleteMember = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: clients.deleteMember,
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.allMembers]
        });
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.allClients]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useDeleteClient = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: clients.deleteClient,
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.allClients]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useVerifyToken = token => {
  const $ = _c(7);
  let t0;
  let t1;
  if ($[0] !== token) {
    t0 = [queryKeys.verifyToken, token];
    t1 = async () => await messages.verifyToken({
      token
    });
    $[0] = token;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  const t2 = token !== undefined;
  let t3;
  if ($[3] !== t0 || $[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      throwOnError: false
    };
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return useQuery(t3);
};
export const useGetAvatarSignedUrl = t0 => {
  const $ = _c(3);
  const {
    clientId,
    userId
  } = t0;
  let t1;
  if ($[0] !== clientId || $[1] !== userId) {
    t1 = {
      mutationFn: async () => {
        if (clientId != null) {
          return await clients.getAvatarSignedUrl(clientId);
        } else {
          return await users.getAvatarSignedUrl(userId as number);
        }
      }
    };
    $[0] = clientId;
    $[1] = userId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useMutation(t1);
};
export const useUploadAvatar = t0 => {
  const $ = _c(4);
  const {
    clientId,
    userId
  } = t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== clientId || $[1] !== queryClient || $[2] !== userId) {
    t1 = {
      mutationFn: _temp3,
      onSuccess: async _ => {
        if (clientId != null) {
          await queryClient.invalidateQueries({
            queryKey: [queryKeys.allClients]
          });
        } else {
          await queryClient.invalidateQueries({
            queryKey: ["userDetails", userId]
          });
        }
      }
    };
    $[0] = clientId;
    $[1] = queryClient;
    $[2] = userId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return useMutation(t1);
};
async function _temp() {
  return await clients.getClients();
}
async function _temp2(t0) {
  const {
    userNotificationIds,
    clientId
  } = t0;
  return await notifications.markNotificationAsRead(clientId, userNotificationIds);
}
async function _temp3(data) {
  return await clients.uploadAvatar(data);
}